import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { Card, CardBody, CardExpandToggler } from '../other/card/card';
import { useGetPerServiceMonthlyDetailedQuery } from '../../redux/apis/analyticsManagement/aMAnalyticsApiSlice';
import ComponentLoader from '../other/ComponentLoader';

const PerServiceStats = () => {
    const { data, isFetching } = useGetPerServiceMonthlyDetailedQuery();
    const [type, setType] = useState("detected");
    const [detectedSeries, setDetectedSeries] = useState([]);
    const [removedSeries, setRemovedSeries] = useState([]);
    const [dataCategories, setDataCategories] = useState([]);

    const colorMap = {
        twitch: "#6441A4",
        snapchat: "#FFFC00",
        facebook: '#3081D0',
        instagram: '#FFC47E',
        twitter: '#000000',
        youtube: '#C70039',
        tiktok: "#69C9D0",
        google: "#4285F4",
        telegram: "#229ED9",
        'ok.ru': "#F97400",
        dailymotion: "#0072FF",
        vimeo: "#1AB7EA",
        bing: "#008373",
        'ugc-source': "#7B2FBE",
        'ugc-download': "#9B59B6",
        'ugc-search': "#E74C3C",
        'ugc-episode': "#2ECC71",
        'ugc-season': "#F39C12",
        ugc_cookies: "#1ABC9C",
        telegram_dmca: "#3498DB",
        vrd_checker: "#E67E22",
        'app-store': "#5BC0DE",
        'brand-protection': "#D9534F",
        'live-streaming': "#5CB85C",
        'blocking_website': "#95A5A6",
        'blocking_websit': "#BDC3C7",
        iptv: "#34495E",
    };

    const fallbackColors = [
        "#4361ee", "#3a0ca3", "#7209b7", "#f72585", "#4cc9f0",
        "#06d6a0", "#ffd166", "#ef476f", "#118ab2", "#e63946",
    ];

    function getAxisTextColor() {
        const appMode = localStorage.getItem('appMode');
        return appMode === 'dark' ? '#ffffff' : '#000000';
    }

    const getStyles = () => {
        const themeFont = getComputedStyle(document.body).getPropertyValue('--bs-body-font-family').trim() || 'Arial, sans-serif';
        const themeFontWeight = getComputedStyle(document.body).getPropertyValue('--bs-body-font-weight').trim();
        const gray600 = getComputedStyle(document.body).getPropertyValue('--bs-gray-600').trim();
        const pink = getComputedStyle(document.body).getPropertyValue('--bs-pink').trim();
        const inverse = getComputedStyle(document.body).getPropertyValue('--bs-inverse').trim();
        const inverseRgb = getComputedStyle(document.body).getPropertyValue('--bs-inverse-rgb').trim();
        return { themeFont, themeFontWeight, gray600, pink, inverse, inverseRgb };
    };

    const { themeFont, themeFontWeight, gray600, pink, inverse, inverseRgb } = getStyles();

    const areaChartOptions = {
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        colors: [pink, gray600],
        grid: { borderColor: "#ddd" },
        yaxis: {
            labels: { style: { colors: getAxisTextColor(), fontSize: '12px', fontFamily: themeFont, fontWeight: themeFontWeight } }
        },
        legend: { fontFamily: themeFont, labels: { colors: inverse } },
        tooltip: { x: { format: 'dd/MM/yy HH:mm' } },
        xaxis: {
            type: "date",
            categories: dataCategories,
            axisTicks: { show: true, borderType: 'solid', color: `rgba(${inverseRgb}, .25)`, height: 6 },
            axisBorder: { show: true, color: `rgba(${inverseRgb}, .25)`, height: 1 },
            labels: { style: { colors: getAxisTextColor(), fontSize: '12px', fontFamily: themeFont, fontWeight: themeFontWeight } },
        },
        chart: { toolbar: { show: false } },
    };

    const convertDateToString = (date) => {
        const year = date.slice(0, 4);
        const month = date.slice(5, 7);
        const monthName = new Date(year, month - 1).toLocaleString('en-us', { month: 'long' }).slice(0, 3);
        const yearShort = year.slice(2, 4);
        return `${monthName} ${yearShort}`;
    };

    const generateData = (data) => {
        if (!data) return;

        const tmpCategories = [];
        const tmpDetectedSeries = [];
        const tmpRemovedSeries = [];
        let categoriesSet = false;
        let fallbackIndex = 0;

        Object.entries(data).forEach(([service, months]) => {
            if (service === 'overall') return;

            const detectedSeriesData = [];
            const removedSeriesData = [];
            const localCategories = [];

            Object.entries(months).forEach(([date, statistics]) => {
                if (date === 'overall') return;

                localCategories.push(convertDateToString(date));
                Object.entries(statistics).forEach(([statType, total_links]) => {
                    if (statType === 'reported') detectedSeriesData.push(total_links);
                    if (statType === 'removed') removedSeriesData.push(total_links);
                });
            });

            if (!categoriesSet) {
                tmpCategories.push(...localCategories);
                categoriesSet = true;
            }

            const hasData = detectedSeriesData.some(v => v > 0) || removedSeriesData.some(v => v > 0);
            if (!hasData) return;

            const color = colorMap[service] || fallbackColors[fallbackIndex++ % fallbackColors.length];
            const displayName = service.charAt(0).toUpperCase() + service.slice(1);
            tmpDetectedSeries.push({ name: displayName, data: detectedSeriesData, color });
            tmpRemovedSeries.push({ name: displayName, data: removedSeriesData, color });
        });

        setDataCategories(tmpCategories);
        setDetectedSeries(tmpDetectedSeries);
        setRemovedSeries(tmpRemovedSeries);
    };

    useEffect(() => {
        generateData(data);
    }, [data]);

    const series = type === 'detected' ? detectedSeries : removedSeries;

    return (
        <div>
            <Card className="mb-3">
                <CardBody className="relative p-0 min-h-20">
                    <div className='p-0'>
                        {isFetching ? <ComponentLoader /> : null}
                        <div className='p-4'>
                            <div className="d-flex justify-content-between mb-3">
                                <span className="fw-bold small">Platform Insights</span>
                                <CardExpandToggler />
                            </div>
                            {series.length > 0 ?
                                <>
                                    <div className="mb-3 flex gap-3">
                                        <button
                                            className={`btn ${type === 'detected' ? 'btn-outline-theme' : 'btn-outline-secondary'}`}
                                            onClick={() => setType('detected')}
                                        >
                                            Reported
                                        </button>
                                        <button
                                            className={`btn ${type === 'removed' ? 'btn-outline-theme' : 'btn-outline-secondary'}`}
                                            onClick={() => setType('removed')}
                                        >
                                            Removed
                                        </button>
                                    </div>
                                    <div className="mb-3 h-full min-h-60">
                                        <Chart type="area" height='100%' options={areaChartOptions} series={series} />
                                    </div>
                                </>
                                :
                                <div className="mb-3 h-full" />
                            }
                        </div>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default PerServiceStats;
